/// ////////////////////////////////////////////////////////////////////////////////

/**
 * Component is used to show a countdown with date based on information from the store-details store
 *
 * @attr {String} [edge-unit] - unit without rounding of its value to the next unit:
 *     - HOURS
 *     - MINUTES
 *     - SECONDS
 * @attr {Boolean} [lead-zero] - add lead zero for every unit
 * @attr {String} [store-code] - an identifier of a store
 * @attr {String} [hours-text-label] - countdown hours text label
 * @attr {String} [minutes-text-label] - countdown minutes text label
 * @attr {String} [seconds-text-label] - countdown seconds text label
 *
 * @prop {String} [date] - date to count down to
 * @prop {Array} [list] - list with data regarding week days openings
 * @prop {Boolean} [open] - if open renders countdown till closing and vice versa
 *
 * <e2-store-countdown
 *     store-code="Store Code"
 *     title-opened="Store opens in"
 *     title-closed="Store closes in"
 *     hours-text-label="h"
 *     minutes-text-label="m"
 *     seconds-text-label="s"
 *     edge-unit="HOURS"
 * ></e2-store-countdown>
 */

import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { LightLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { actions, selectors } from '@elab/core/store/store-details';
import { relationalOperatorsConstants, timeUnitConstants } from '@elab/core/utils/constants';
import {
    checkIfValidendDateAndTime,
    deadlineDateUtil,
    defineEndDate,
    endDayCheckUtil,
    getBeforeTimePointUtil,
    getCurrentDayNumber,
    getEndDateAndTime,
    getStartDateAndTime,
    getTimeRemaining,
    initializeCountdownTimer,
    isBetweenTwoTimes,
} from '@elab/core/utils/countdownUtils';

class StoreCountdown extends LightLitElement {
    static get properties() {
        return {
            endDateAndTime: { type: String, attribute: false },
            format: { type: String, attribute: 'format' },
            edgeUnit: { type: String, attribute: 'edge-unit' },
            days: { type: Number },
            hours: { type: Number },
            minutes: { type: Number },
            seconds: { type: Number },
            date: { type: String, attribute: false },
            list: { type: Array, attribute: false },
            open: { type: Boolean, attribute: false },
            oneHourToClosing: { type: Boolean, attribute: false },
            storeCode: { type: String, attribute: 'store-code' },
            titleOpen: { type: String, attribute: 'title-open' },
            titleClose: { type: String, attribute: 'title-closed' },
            initialized: { type: Boolean, reflect: true },
        };
    }

    constructor() {
        super();
        this.endDateAndTime = '';
        this.format = '';
        this.days = 0;
        this.hours = 0;
        this.minutes = 0;
        this.seconds = 0;
        this.edgeUnit = '';
        this.showDays = false;
        this.showHours = true;
        this.showMinutes = true;
        this.showSeconds = true;
        this.date = false;
        this.list = [];
        this.open = false;
        this.oneHourToClosing = false;
        this.titleClose = '';
        this.titleOpen = '';
        this.textLabel = ':';
        this.initialized = false;
    }

    afterConnect() {
        this.setTimeInterval(this.updateClock, 1000);
        if (this.storeCode) {
            this.dispatchEvent(new CustomEvent('get-store-details'));
        }
    }

    updated(_changedProperties) {
        if (_changedProperties.has('storeCode')) {
            this.dispatchEvent(new CustomEvent('get-store-details'));
            this.updateClock();
        }
        if (_changedProperties.has('endDateAndTime')) {
            this.updateClock();
        }
        if (_changedProperties.has('list') && this.list.length) {
            this.updateDate();
            this.initialized = true;
        }
    }

    disconnectedCallback() {
        clearInterval(this.timeinterval);
    }

    setTimeInterval(callback, time) {
        clearInterval(this.timeinterval);
        this.timeinterval = initializeCountdownTimer(callback, time);
    }

    updateClock = () => {
        // prevents the clock to be loaded from storeinfowindow if list not populated in store
        this.date = checkIfValidendDateAndTime(this.endDateAndTime);
        if (!this.date) {
            return;
        }

        const { days, hours, minutes, seconds } = getTimeRemaining(
            this.deadlineDate,
            this.edgeUnit,
        );
        if (days) {
            this.days = days < 10 ? `0${days}` : days;
            this.showDays = true;
        } else {
            this.days = '';
            this.showDays = false;
        }
        this.hours = hours < 10 ? `0${hours}` : hours;
        this.minutes = minutes < 10 ? `0${minutes}` : minutes;
        this.seconds = seconds < 10 ? `0${seconds}` : seconds;

        if (
            this.open &&
            endDayCheckUtil(
                this.endDateAndTime,
                timeUnitConstants.seconds,
                0,
                relationalOperatorsConstants.lessThan,
            )
        ) {
            clearInterval(this.timeinterval);
            this.oneHourToClosing = false;
            this.open = false;
            this.updateDate();
        }

        if (
            this.open &&
            endDayCheckUtil(
                this.endDateAndTime,
                timeUnitConstants.hours,
                1,
                relationalOperatorsConstants.lessThan,
            ) &&
            endDayCheckUtil(
                this.endDateAndTime,
                timeUnitConstants.minutes,
                59.9,
                relationalOperatorsConstants.moreThan,
            )
        ) {
            this.oneHourToClosing = true;
            this.updateDate();
        }

        if (
            !this.open &&
            endDayCheckUtil(
                this.endDateAndTime,
                timeUnitConstants.seconds,
                0,
                relationalOperatorsConstants.lessThan,
            )
        ) {
            clearInterval(this.timeinterval);
            this.open = true;
            this.updateDate();
        }
    };

    get deadlineDate() {
        return deadlineDateUtil(defineEndDate(this.endDateAndTime));
    }

    getDayData(weekDay) {
        if (this.list) {
            let targetDay;
            targetDay = weekDay !== undefined ? weekDay : getCurrentDayNumber();
            return this.list[targetDay];
        }
        return (this.date = false);
    }

    isStoreOpened(openingTime, closingTime) {
        const { closed } = this.getDayData();

        return (
            !closed &&
            openingTime &&
            closingTime &&
            isBetweenTwoTimes(
                openingTime.hour,
                openingTime.minute,
                closingTime.hour,
                closingTime.minute,
            )
        );
    }

    isStoreOneHourAwayFromClosing(closingTime) {
        const { closed } = this.getDayData();

        return (
            !closed &&
            closingTime &&
            isBetweenTwoTimes(
                closingTime.hour - 1,
                closingTime.minute,
                closingTime.hour,
                closingTime.minute,
            )
        );
    }

    getStoreTime(storeTime) {
        const hour = storeTime && storeTime.hour ? storeTime.hour : 0;
        const minute = storeTime && storeTime.minute ? storeTime.minute : 0;
        return [hour, minute];
    }

    formatStoreTime(storeTime) {
        const [hour, minute] = this.getStoreTime(storeTime);
        return `${hour}:${minute < 10 ? '0' : ''}${minute}`;
    }

    checkStoreOpenInterval() {
        const { closed, openingTime, closingTime, openingTimeIntervals = [] } = this.getDayData();

        let storeOpeningTime = false;
        let storeClosingTime = false;

        if (!closed) {
            if (openingTimeIntervals.length) {
                const storeCurrentIntervalTime = openingTimeIntervals.find(
                    interval =>
                        interval.openingTime &&
                        interval.closingTime &&
                        isBetweenTwoTimes(
                            interval.openingTime.hour,
                            interval.openingTime.minute,
                            interval.closingTime.hour,
                            interval.closingTime.minute,
                        ),
                );
                if (storeCurrentIntervalTime) {
                    storeOpeningTime = storeCurrentIntervalTime.openingTime;
                    storeClosingTime = storeCurrentIntervalTime.closingTime;
                }
            } else {
                storeOpeningTime = openingTime;
                storeClosingTime = closingTime;
            }
        }

        if (
            storeOpeningTime &&
            storeOpeningTime.formattedHour &&
            storeClosingTime &&
            storeClosingTime.formattedHour &&
            isBetweenTwoTimes(
                storeOpeningTime.hour,
                storeOpeningTime.minute,
                storeClosingTime.hour,
                storeClosingTime.minute,
            )
        ) {
            this.open = this.isStoreOpened(storeOpeningTime, storeClosingTime);
            this.oneHourToClosing = this.isStoreOneHourAwayFromClosing(storeClosingTime);
            this.endDateAndTime = getEndDateAndTime(this.formatStoreTime(storeClosingTime));

            return;
        }

        this.open = false;
        this.oneHourToClosing = false;
    }

    updateDate() {
        this.checkStoreOpenInterval();

        if (!this.open) {
            this.updateDateToOpenToday();
        }
    }

    updateDateToOpenToday() {
        const { closed, openingTime, closingTime, openingTimeIntervals = [] } = this.getDayData();

        let storeOpeningTime = false;
        let storeClosingTime = false;

        if (!closed) {
            if (openingTimeIntervals.length) {
                const storeNextIntervalTime = openingTimeIntervals.find(
                    interval =>
                        interval.openingTime &&
                        interval.closingTime &&
                        getBeforeTimePointUtil(
                            interval.openingTime.hour,
                            interval.openingTime.minute,
                        ),
                );
                if (storeNextIntervalTime) {
                    storeOpeningTime = storeNextIntervalTime.openingTime;
                    storeClosingTime = storeNextIntervalTime.closingTime;
                }
            } else {
                if (openingTime && closingTime) {
                    storeOpeningTime = openingTime;
                    storeClosingTime = closingTime;
                }
            }
        }

        if (
            storeOpeningTime &&
            storeOpeningTime.formattedHour &&
            storeClosingTime &&
            storeClosingTime.formattedHour &&
            getBeforeTimePointUtil(storeOpeningTime.hour, storeOpeningTime.minute)
        ) {
            this.open = false;
            this.endDateAndTime = getStartDateAndTime(this.formatStoreTime(storeOpeningTime), 0);
            return;
        }

        this.updateDateToOpenClosest();
    }

    updateDateToOpenClosest() {
        const currentDayNumber = getCurrentDayNumber();

        let storeClosestOpeningTime = false;

        const dayShift =
            [
                ...this.list.slice(currentDayNumber + 1),
                ...this.list.slice(0, currentDayNumber + 1),
            ].findIndex(dayData => {
                let storeOpeningTime = false;
                let storeClosingTime = false;

                if (!dayData.closed) {
                    if (dayData.openingTimeIntervals && dayData.openingTimeIntervals.length) {
                        dayData.openingTimeIntervals.forEach(interval => {
                            if (
                                !storeOpeningTime ||
                                interval.openingTime.hour < storeOpeningTime.hour
                            ) {
                                storeOpeningTime = interval.openingTime;
                                storeClosingTime = interval.closingTime;
                            }
                        });
                    } else {
                        storeOpeningTime = dayData.openingTime;
                        storeClosingTime = dayData.closingTime;
                    }
                }

                if (
                    storeOpeningTime &&
                    storeOpeningTime.formattedHour &&
                    storeClosingTime &&
                    storeClosingTime.formattedHour &&
                    (storeOpeningTime.hour < storeClosingTime.hour ||
                        (storeOpeningTime.hour === storeClosingTime.hour &&
                            storeOpeningTime.minute < storeClosingTime.minute))
                ) {
                    storeClosestOpeningTime = storeOpeningTime;
                    return true;
                }

                return false;
            }) + 1;

        if (dayShift < 0) {
            this.date = false;
            return;
        }

        if (storeClosestOpeningTime && dayShift > 0) {
            this.open = false;
            this.endDateAndTime = getStartDateAndTime(
                this.formatStoreTime(storeClosestOpeningTime),
                dayShift,
            );
            return;
        }

        this.endDateAndTime = '';
    }

    get getStoreCountdownClasses() {
        return classMap({
            'store-countdown__wrapper': true,
            'd-contents': this.date,
            'd-none': !this.date,
            'store-countdown--has-days': this.showDays,
            'countdown--store--one-hour-to-close': this.open && this.oneHourToClosing,
            'countdown--store--close': !this.open,
            'countdown--store--open': this.open && !this.oneHourToClosing,
        });
    }

    get countdownTemplate() {
        return html`
            ${this.getCountdownItemTemplate(this.showDays, this.days, this.textLabel)}
            ${this.getCountdownItemTemplate(this.showHours, this.hours, this.textLabel)}
            ${this.getCountdownItemTemplate(this.showMinutes, this.minutes, this.textLabel)}
            ${this.getCountdownItemTemplate(this.showSeconds, this.seconds)}
        `;
    }

    getCountdownItemTemplate(isPresent, value, textLabel) {
        return isPresent
            ? html`
                  <div class="countdown-timer__element">
                      <div class="countdown-timer__element-value">
                          ${this.getCountdownElementText(value, textLabel)}
                      </div>
                  </div>
              `
            : html``;
    }

    getCountdownElementText(value, label = '') {
        return html`
            <span class="countdown-timer__element-counter">${value}</span>
            <span class="countdown-timer__element-unit">${label}</span>
        `;
    }

    getCountdownTemplate() {
        const deadlinePassed = endDayCheckUtil(
            this.endDateAndTime,
            timeUnitConstants.seconds,
            0,
            relationalOperatorsConstants.lessThan,
        );

        const titleTemplate = this.open
            ? html`
                  <h3 class="countdown__title 'd-contents'">
                      ${this.titleOpen}
                  </h3>
              `
            : html`
                  <h3 class="countdown__title 'd-contents'">
                      ${this.titleClose}
                  </h3>
              `;

        if (deadlinePassed) {
            return html`
                <h3 class="countdown__title 'd-contents'">
                    <v-slot name="text"></v-slot>
                </h3>
            `;
        }
        return html`
            <div class="countdown__content">
                <h3 class="countdown__title 'd-contents'">
                    ${titleTemplate}
                </h3>
                <div id="countdown-timer" class="countdown-timer">
                    ${this.countdownTemplate}
                </div>
            </div>
        `;
    }

    render() {
        // change icon to clock icon (as in production) once the correct icon will be available
        return html`
            <div class="${this.getStoreCountdownClasses}">
                <div class="countdown__wrapper--with-icon">
                    <i class="icon icon-home icon-home--countdown"></i>
                    <div class="countdown">
                        <div class="countdown__container">
                            ${this.getCountdownTemplate()}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}

const mapStateToProps = (state, ctx) => ({
    storeCode: ctx.storeCode,
    list: selectors.getWeekDayOpeningList(state),
});

const mapDispatchToEvents = (dispatch, ctx) => ({
    'get-store-details': () => dispatch(actions.getStoreDetails(ctx.storeCode)),
});

export default () =>
    customElements.define(
        'e2-store-countdown',
        connect(mapStateToProps, mapDispatchToEvents)(StoreCountdown),
    );
